import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Container } from 'react-bootstrap'
import './Notes.css'
import './NotePdfExport.css'
import NotesMainContent from '@components/pages/Notes/NotesMainContent.component'
import type {
  NotesLocationState,
  PreviousPageState,
} from '@components/common/NoteEditorOverlay/notesFeature.types'
import NotesFeatureMainColumn from '@components/common/NoteEditorOverlay/NotesFeatureMainColumn.component'
import { useNotesEditorFeature } from '@components/common/NoteEditorOverlay/useNotesEditorFeature'
import TranscribedAudioPanel from '@components/pages/Notes/TranscribedAudioPanel.component'
import { Patient } from '@dataTypes/PatientInfo.types'
import { NoteContextType } from '@dataTypes/note.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchAllNotes } from '@store/actions'
import {
  clearSelectedPatient,
  setSelectedPatient,
} from '@store/reducers/patientmanagement.reducer'
import { requestPatientSelection } from '@store/reducers/uiSlice.reducer'
import { fetchPatientById } from '@services/api/patient.api'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import { getFullName } from '@utils/fullname.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import NotesPendingLeaveModal from '@components/common/NoteEditorOverlay/NotesPendingLeaveModal.component'

const Notes: React.FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const {
    data: notes,
    error,
    loading: notesLoading,
  } = useAppSelector((state) => state.note)
  const patientList = useAppSelector((state) => state.patient.data ?? [])
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)

  const [pagePreviousState, setPagePreviousState] =
    useState<PreviousPageState | null>(null)
  const [originContextId, setOriginContextId] = useState<string | null>(null)
  const [originContactId, setOriginContactId] = useState<string | null>(null)
  const [originContextType, setOriginContextType] =
    useState<NoteContextType | null>(null)
  const [originContextName, setOriginContextName] = useState<string | null>(
    null,
  )
  const [originOpenFirstNote, setOriginOpenFirstNote] = useState<boolean>(false)
  const [originInitialTitle, setOriginInitialTitle] = useState<string | null>(
    null,
  )
  const lastFetchedPatientIdRef = useRef<string | null>(null)
  /** Tracks location state we already applied (navigated for) to avoid effect→navigate→effect loop (e.g. charting→view notes). */
  const appliedLocationStateKeyRef = useRef<string | null>(null)
  /** Patient IDs currently being resolved via fetchPatientById for deep links. */
  const resolvingPatientIdRef = useRef<string | null>(null)

  const selectedPatientName = useMemo(
    () => (selectedPatient ? (getFullName(selectedPatient) ?? '') : ''),
    [selectedPatient],
  )

  const locationState = location.state as NotesLocationState | undefined
  const syncOpenNoteId = locationState?.openNoteId
  const requiresPatientForCurrentFlow = Boolean(locationState?.patientId)

  const nf = useNotesEditorFeature({
    isOpen: true,
    selectedPatient,
    selectedPatientName,
    patientPreviousPageState: pagePreviousState,
    notes,
    notesLoading,
    selectedClinic,
    dispatch,
    showToast,
    showLoading,
    hideLoading,
    navigate,
    originContextId,
    originContactId,
    originContextType,
    originContextName,
    originOpenFirstNote,
    syncOpenNoteId,
    requiresPatientForCurrentFlow,
    loadingContainerSelector: '.notes-loading-container',
    sidebarCollapsible: false,
  })

  /**
   * Sync a deep-link patientId into the global tray (Redux selectedPatient).
   * Resolves from the patient list when available; otherwise fetches by id.
   * Clears the active tray patient when patientId is absent so leftover
   * selection is not used for fetch or save. Does not wipe the tray list.
   */
  const syncTrayPatientFromId = useCallback(
    (patientId: string | undefined): void => {
      if (!patientId || patientId.trim() === '') {
        if (selectedPatient?.patient_id) {
          dispatch(clearSelectedPatient())
        }
        return
      }
      if (patientId === selectedPatient?.patient_id) return

      const fromList = patientList.find((p) => p.patient_id === patientId)
      if (fromList) {
        dispatch(setSelectedPatient({ patient: fromList }))
        return
      }

      if (resolvingPatientIdRef.current === patientId) return
      resolvingPatientIdRef.current = patientId
      void fetchPatientById(patientId, true)
        .then((res) => {
          const patient = res.data?.patient as Patient | null
          if (patient?.patient_id) {
            dispatch(setSelectedPatient({ patient }))
          }
        })
        .finally(() => {
          if (resolvingPatientIdRef.current === patientId) {
            resolvingPatientIdRef.current = null
          }
        })
    },
    [dispatch, patientList, selectedPatient?.patient_id],
  )

  // Set context from location state (e.g. View note from Charting). Filter is applied in NoteEditor only, not here.
  useEffect(() => {
    const state = location.state as NotesLocationState | undefined
    if (!state) return
    // Deep-link from a note notification (e.g. "Note edited" → View Note):
    // treat it as a flat patient-context view so the page title and Back
    // behaviour all work normally. Origin context fields are intentionally
    // suppressed here — otherwise a contextful note (e.g. charting) would
    // hide the main list and the "Notes for {patient}" header would resolve
    // to a context name instead of the patient.
    const isNotificationDeepLink = Boolean(state.openNoteId)
    if (
      !isNotificationDeepLink &&
      state.contextId != null &&
      state.contextId.trim() !== ''
    ) {
      setOriginContextId(state.contextId)
    } else {
      setOriginContextId(null)
    }
    if (!isNotificationDeepLink && state.contextType != null) {
      setOriginContextType(state.contextType)
    } else {
      setOriginContextType(null)
    }
    if (
      !isNotificationDeepLink &&
      state.contextName != null &&
      state.contextName.trim() !== ''
    ) {
      setOriginContextName(state.contextName)
    } else {
      setOriginContextName(null)
    }
    setOriginOpenFirstNote(Boolean(state.openFirstNote))
    if (state.initialTitle != null && state.initialTitle.trim() !== '') {
      setOriginInitialTitle(state.initialTitle)
    } else {
      setOriginInitialTitle(null)
    }
  }, [location.state])

  useEffect(() => {
    const state = location.state as NotesLocationState | undefined
    if (!state) {
      appliedLocationStateKeyRef.current = null
      return
    }

    const hasPreviousPage =
      !!state.previousPage && typeof state.previousPage.path === 'string'
    if (hasPreviousPage && state.previousPage) {
      setPagePreviousState(state.previousPage)
    }

    const preservedState: Partial<NotesLocationState> = {
      ...(state.previousPage && { previousPage: state.previousPage }),
      ...(state.patientId && { patientId: state.patientId }),
      ...(state.contactId && { contactId: state.contactId }),
      ...(state.contextId && { contextId: state.contextId }),
      ...(state.contextType && { contextType: state.contextType }),
      ...(state.openNoteId && { openNoteId: state.openNoteId }),
      ...(state.createNewNoteForCall &&
        state.callId && {
          contextId: state.callId,
          contextType: NoteContextType.CALL,
        }),
      ...(state.contextName && { contextName: state.contextName }),
      ...(state.openFirstNote && { openFirstNote: state.openFirstNote }),
      ...(state.fromNotesHome && { fromNotesHome: state.fromNotesHome }),
      ...(state.initialTitle && { initialTitle: state.initialTitle }),
    }
    const stateKey = JSON.stringify(preservedState)

    const applyAndMaybeNavigate = (): void => {
      if (appliedLocationStateKeyRef.current === stateKey) return
      appliedLocationStateKeyRef.current = stateKey
      navigate('/notes', { replace: true, state: preservedState })
    }

    // Deep-link from a note notification (e.g. "Note edited" → View Note).
    // Select the patient and reset editor state — the open-by-id effect inside
    // useNotesEditorFeature will flip the page to view mode once notes load.
    // Important: do NOT call applyRouteOpenComposer here; that briefly opens
    // add-mode and can race the view switch.
    if (state.openNoteId && state.patientId) {
      syncTrayPatientFromId(state.patientId)
      nf.applyRouteNotesHomeList()
      applyAndMaybeNavigate()
      return
    }

    // Back to notes home: show list with selected patient, do not open add form (no navigate here to avoid re-running effect)
    if (state.fromNotesHome && state.patientId) {
      syncTrayPatientFromId(state.patientId)
      nf.applyRouteNotesHomeList()
      return
    }

    if (state.createNewNoteForCall && state.callId) {
      syncTrayPatientFromId(state.patientId)
      setOriginContextId(state.callId)
      setOriginContextType(NoteContextType.CALL)
      setOriginContactId(state.contactId ?? null)
      nf.applyRouteOpenComposer(false)
      applyAndMaybeNavigate()
      return
    }

    // Add note / View notes from Message Center Calls: open Notes page with call context (contextId = call_id, contextType = CALL). Check before contactId so call context wins when both are present.
    if (
      state.contextType === NoteContextType.CALL &&
      state.contextId != null &&
      state.contextId.trim() !== ''
    ) {
      setOriginContextId(state.contextId.trim())
      setOriginContextType(NoteContextType.CALL)
      setOriginContactId(state.contactId ?? null)
      syncTrayPatientFromId(state.patientId)
      nf.applyRouteOpenComposer(true)
      applyAndMaybeNavigate()
      return
    }

    // Add note / View notes from Message Center Contacts: contact only (e.g. provider) or contact + patient. When openFirstNote is true (View from Contacts), NoteEditor selects first note instead of create form.
    if (state.contactId) {
      setOriginContactId(state.contactId)
      setOriginContextType(NoteContextType.CONTACT)
      setOriginContextId(null)
      syncTrayPatientFromId(state.patientId)
      nf.applyRouteOpenComposer(true)
      applyAndMaybeNavigate()
      return
    }

    if (state.patientId) {
      syncTrayPatientFromId(state.patientId)
      // When contextId is present (e.g. View note from Charting), open NoteEditor immediately with note list sidebar
      const hasContext =
        state.contextId != null && state.contextId.trim() !== ''
      nf.applyRouteOpenComposer(hasContext) // show sidebar when from Charting so user sees filtered note list
      applyAndMaybeNavigate()
    } else if (hasPreviousPage) {
      applyAndMaybeNavigate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable route callbacks only; full `nf` reruns unnecessarily
  }, [
    location.state,
    navigate,
    patientList,
    syncTrayPatientFromId,
    nf.applyRouteNotesHomeList,
    nf.applyRouteOpenComposer,
  ])

  useEffect(() => {
    const patientId = selectedPatient?.patient_id ?? null
    if (!patientId) {
      lastFetchedPatientIdRef.current = null
      return
    }
    if (lastFetchedPatientIdRef.current === patientId) return
    lastFetchedPatientIdRef.current = patientId
    showLoading({
      container: '.notes-loading-container',
    })
    dispatch(fetchAllNotes({ patientId }))
      .unwrap()
      .then(() => {
        hideLoading('.notes-loading-container')
      })
      .catch(() => {
        hideLoading('.notes-loading-container')
      })
  }, [dispatch, selectedPatient?.patient_id, showLoading, hideLoading])

  const transcriptionSegments = nf.transcriptionSegments

  return (
    <Container fluid>
      <div className="notes-page-layout">
        <div className="notes-feature-container">
          <div className="notes-outer notes-loading-container">
            {!nf.isAddingNote && !nf.isEditingNote && !nf.isViewingNote && (
              <div className="notes-top-row notes-top-row-sidebar-closed">
                <div className="notes-sidebar-top">
                  <div className="notes-sidebar-top-inner">
                    <div className="nsti-title">
                      {originContextName
                        ? `Notes for ${originContextName}`
                        : selectedPatientName
                          ? `Notes for ${selectedPatientName}`
                          : 'Notes'}
                    </div>
                  </div>
                </div>
                <div className="notes-main-content-top">
                  <NotesMainContent
                    selectedNotes={nf.selectedPatientNotes}
                    patientName={selectedPatientName || 'Select a patient'}
                    onAddNote={nf.handleAddNote}
                    onViewNote={nf.handleViewNote}
                    onEditNote={nf.handleEditNote}
                    onDeleteNote={nf.handleDeleteNote}
                    onDownloadNote={nf.handleDownloadNote}
                    renderTopOnly={true}
                    searchQuery={nf.noteSearchQuery}
                    onSearchChange={nf.handleNoteSearchChange}
                    filters={nf.noteFilters}
                    onFilterChange={nf.handleNoteFilterChange}
                    onResetFilters={nf.handleNoteResetFilters}
                    presetContextType={originContextType ?? undefined}
                  />
                </div>
              </div>
            )}
            <div className="notes-inner">
              <NotesFeatureMainColumn
                feature={nf}
                selectedPatient={selectedPatient}
                selectedPatientName={selectedPatientName}
                patientPreviousPageState={pagePreviousState}
                originContextId={originContextId}
                originContactId={originContactId}
                originContextType={originContextType}
                originContextName={originContextName}
                originOpenFirstNote={originOpenFirstNote}
                originInitialTitle={originInitialTitle}
                notesLoading={notesLoading}
                notesError={error ?? undefined}
                showPatientSideSlot={true}
                // When deep-linked to a specific note (e.g. from a "Note edited" notification),
                // surface the versions/notes sidebar so the version chain ("- vN" rows) is visible.
                openContextNotesByDefault={Boolean(syncOpenNoteId)}
                onSelectPatient={(): void => {
                  dispatch(requestPatientSelection())
                }}
              />
            </div>
          </div>
        </div>
        {nf.showTranscribedAudio && (nf.isAddingNote || nf.isEditingNote) && (
          <TranscribedAudioPanel segments={transcriptionSegments} />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={nf.showDeleteConfirmation}
        onHide={nf.handleCancelDelete}
        onConfirm={nf.handleConfirmDelete}
        entityName={nf.noteToDelete?.title || 'this note'}
      />

      <NotesPendingLeaveModal
        pendingLeave={nf.pendingLeave}
        onDismiss={nf.handleCancelLeavePage}
        onDiscard={nf.handleDiscardAndLeavePage}
        onSave={() => void nf.handleSaveAndLeavePage()}
      />
    </Container>
  )
}

export default Notes
